/**
 * Centralised display labels for canonical entity roles and side alignments.
 *
 * Side labels come from the jurisdiction JSON config (single source of truth).
 * Role labels are jurisdiction-agnostic.
 */

import type { EntityRole } from '@/hooks/useEntities';
import { getJurisdiction, type UiLabels } from '@/jurisdictions';

const ROLE_LABELS: Record<string, string> = {
  party: 'Party',
  amicus: 'Amicus',
  legal_representative: 'Legal Representative',
  witness: 'Witness',
  expert: 'Expert',
  adjudicator: 'Adjudicator',
  judge: 'Judge',
  arbitrator: 'Arbitrator',
  court: 'Court',
  tribunal: 'Tribunal',
  other: 'Other',
  unknown: 'Unknown',
};

/** Lower number = higher priority. */
const ROLE_PRIORITY: Record<string, number> = {
  party: 0,
  amicus: 1,
  legal_representative: 1,
  witness: 2,
  expert: 3,
  adjudicator: 4,
  court: 5,
  tribunal: 6,
  judge: 7,
  arbitrator: 8,
  other: 9,
  unknown: 10,
};

const DEFAULT_UI_LABELS = getJurisdiction('UK').ui_labels;
const PRINCIPAL_ROLE_TYPES = new Set(['party', 'amicus']);

export function roleLabel(roleType: string): string {
  return ROLE_LABELS[roleType] ?? roleType.replace(/_/g, ' ');
}

export function sideLabel(sideAlignment: string, labels: UiLabels = DEFAULT_UI_LABELS): string {
  if (sideAlignment === 'claimant_side') return labels.claimant_side;
  if (sideAlignment === 'respondent_side') return labels.respondent_side;
  if (sideAlignment === 'neutral' || sideAlignment === 'unknown') return 'Other / Unknown';
  return sideAlignment.replace(/_/g, ' ');
}

export function roleWithSide(
  roleType: string,
  sideAlignment: string,
  labels: UiLabels = DEFAULT_UI_LABELS,
): string {
  const role = roleLabel(roleType);
  const side = sideLabel(sideAlignment, labels);
  return side ? `${role} · ${side}` : role;
}

/** Sort roles by priority (party first, then legal rep, witness, etc.) */
export function sortedRoles(roles: EntityRole[]): EntityRole[] {
  return [...roles].sort(
    (a, b) => (ROLE_PRIORITY[a.role_type] ?? 99) - (ROLE_PRIORITY[b.role_type] ?? 99),
  );
}

export function hasPrincipalRole(roles: EntityRole[]): boolean {
  return roles.some(role => PRINCIPAL_ROLE_TYPES.has(role.role_type));
}

export function displayRoles(roles: EntityRole[]): EntityRole[] {
  const orderedRoles = sortedRoles(roles);
  if (!hasPrincipalRole(orderedRoles)) return orderedRoles;
  return orderedRoles.filter(role => role.role_type !== 'legal_representative');
}
